import React, { useEffect } from 'react';
import cc from 'classcat';
import { faEye } from '@fortawesome/pro-solid-svg-icons';

import Icon from '../../atoms/icon';
import Tooltip from '../tooltip';
import { usePatientSocket } from '../../../contexts/socket/patient';
import { SocketUser } from '../../../contexts/socket';

interface PatientIdProps {
  patientId: number;
}

interface RoomViewerProps extends PatientIdProps {
  className?: string;
}

interface RoomWrapperProps extends PatientIdProps {
  className?: string;
  borderWidth?: string;
  includeMembers?: boolean;
  showTooltip?: boolean;
  joinRoom?: boolean;
}

interface MemberProps {
  member: SocketUser;
  isLastMember: boolean;
}

const Member: React.FC<MemberProps> = ({ member, isLastMember }) => {
  return (
    <li
      className={`inline-block list-none text-sans-sm${
        isLastMember ? '' : ' mr-1'
      }`}
    >
      {member.email}
      {isLastMember ? '' : ', '}
    </li>
  );
};

export const RoomWrapper: React.FC<RoomWrapperProps> = ({
  patientId,
  className,
  borderWidth = '1',
  children,
  includeMembers = false,
  showTooltip = false,
  joinRoom = false
}) => {
  const {
    enterPatientRoom,
    leavePatientRoom,
    getPatientRoomMembers,
    isCurrentUserInRoom
  } = usePatientSocket();

  const classes = cc([
    'border-blue border-solid relative',
    `border-${borderWidth}`,
    className ? className : undefined
  ]);

  useEffect(() => {
    if (!isCurrentUserInRoom(patientId) && joinRoom && patientId !== null) {
      enterPatientRoom(patientId);
      return () => {
        leavePatientRoom(patientId);
      };
    }
  }, [patientId]);

  const members = getPatientRoomMembers(patientId);

  if (!members || !members.length || patientId === null) {
    return <div className={className}>{children}</div>;
  }

  const output = (
    <div className={classes}>
      {includeMembers ? null : (
        <div className="absolute-center bg-overlay-white pointer-events-none z-1"></div>
      )}
      {includeMembers ? (
        <div className="bg-blue text-white px-2 pb-2">
          <span className="inline-block text-sans-sm font-bold">
            USERS VIEWING:
          </span>
          <div className="inline-block ml-2">
            <ul className="pl-0 ma-0">
              {members.map((member, index) => (
                <Member
                  key={member.email}
                  member={member}
                  isLastMember={members.length === index + 1}
                />
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );

  if (!showTooltip) {
    return output;
  }

  let content = 'USERS VIEWING: ';
  content += members
    .slice(0, 2)
    .map((member) => member.email.toLowerCase())
    .join(', ');
  if (members.length > 2) {
    content += `, AND ${members.length - 2}+`;
  }
  return (
    <Tooltip content={content} targetTagName="w-full">
      {output}
    </Tooltip>
  );
};

export const RoomViewer = ({
  patientId,
  className = ''
}: RoomViewerProps): JSX.Element => {
  const { getPatientRoomMembers } = usePatientSocket();

  const classes = cc(['text-blue', className ? className : undefined]);

  const members = getPatientRoomMembers(patientId);

  if (!members || !members.length) {
    return null;
  }

  return <Icon icon={faEye} className={classes} />;
};
